/* Permissions — the client's own left-menu item, beside Groups.
 *
 * From page 2 of their Groups&Subgroups document, which draws two tabs:
 *
 *   MODERATORS — Add opens Name / Mobile / Email / Role / Save, and their
 *   annotation reads "(Auto populate) from group list members". That matters:
 *   a moderator is CHOSEN from people already in the church, not typed in
 *   fresh. So Name searches the members, and Mobile and Email fill in from
 *   whoever is picked. Who counts as a moderator is the server's one
 *   definition — see list_moderators — so this tab and the call form's
 *   moderator choices agree.
 *
 *   ROLES — Add opens Name ("Moderator-1" is their example), Description, and
 *   a checkbox list of permissions.
 *
 * THE PERMISSION LIST COMES FROM THE API. Their document names one permission,
 * "Viewing other members", and annotates the rest "As used in slack" — so the
 * vocabulary is ours to propose. It is served from /groups/permission-catalogue
 * rather than hard-coded here, so this screen and the validator that rejects
 * unknown permissions can never disagree about what exists.
 */
import { useMemo, useState } from 'react'
import { Check, Loader2, Pencil, Plus, Search, Shield, Trash2, UserMinus } from 'lucide-react'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Confirm } from '@/components/ui/confirm'
import { Field, SelectField } from '@/components/ui/field'
import { Modal } from '@/components/ui/modal'
import {
  useChurch,
  useCreateRole,
  useDeleteRole,
  useMe,
  useMemberSearch,
  useModerators,
  usePermissionCatalogue,
  useRoles,
  useSetModerator,
  useUpdateRole,
} from '@/api/queries'
import { ApiError } from '@/api/client'
import { useDebounced } from '@/lib/useDebounced'
import { useTabParam } from '@/lib/useTabParam'
import { cn } from '@/lib/utils'
import type { ChurchRole, Member, Moderator } from '@/types/api'

const TABS = ['moderators', 'roles'] as const
type Tab = (typeof TABS)[number]

/* The role every member is given on joining. Not offered as a moderator's
   role: choosing it is what Remove does. */
const MEMBER_ROLE = 'Participant'


export function PermissionsPage() {
  const { data: me } = useMe()
  const churchId = me?.church_id ?? null
  const church = useChurch(churchId)
  const [tab, setTab] = useTabParam<Tab>(TABS, 'moderators')

  if (!me) return null

  if (!churchId) {
    return (
      <Card className="p-s8 text-center">
        <Shield className="mx-auto h-8 w-8 text-ink-2" aria-hidden />
        <p className="mt-s3 text-md font-bold">No church to administer</p>
        <p className="mt-1 text-base text-ink-2">
          Permissions belong to a church.
        </p>
      </Card>
    )
  }

  return (
    <div className="space-y-s4">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">Permissions</h1>
        <p className="mt-1 text-base text-ink-2">{church.data?.name ?? '…'}</p>
      </header>

      <div className="flex gap-s1 border-b border-line-2">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            aria-current={tab === t ? 'page' : undefined}
            className={cn(
              '-mb-px border-b-2 px-s4 py-s3 text-base font-semibold transition-colors',
              tab === t
                ? 'border-brand text-brand'
                : 'border-transparent text-ink-2 hover:text-ink',
            )}
          >
            {t === 'moderators' ? 'Moderators' : 'Roles'}
          </button>
        ))}
      </div>

      {tab === 'moderators' ? (
        <Moderators churchId={churchId} myId={me.id} />
      ) : (
        <Roles churchId={churchId} />
      )}
    </div>
  )
}

/* ------------------------------------------------------------- moderators */

function Moderators({ churchId, myId }: { churchId: string; myId: string }) {
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<Moderator | null>(null)
  const moderators = useModerators(churchId)
  const roles = useRoles(churchId)
  const setModerator = useSetModerator(churchId)
  const roleOptions = (roles.data ?? [])
    .filter((r) => r.name !== MEMBER_ROLE)
    .map((r) => ({ value: r.id, label: r.name }))

  return (
    <div className="space-y-s3">
      <div className="flex justify-end">
        <Button variant="primary" onClick={() => setAdding(true)}>
          <Plus className="h-4 w-4" aria-hidden />
          Add moderator
        </Button>
      </div>

      {moderators.isLoading ? (
        <div className="flex justify-center py-s6">
          <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
        </div>
      ) : null}

      {moderators.isError ? (
        <Card className="p-s4 text-center text-base text-ink-2">
          Could not load the moderators.
        </Card>
      ) : null}

      {setModerator.isError ? (
        <p role="alert" className="rounded-md bg-danger-soft px-s3 py-s2 text-base text-danger-ink">
          {setModerator.error instanceof ApiError
            ? setModerator.error.message
            : 'That did not save.'}
        </p>
      ) : null}

      {moderators.data && !moderators.data.some((m) => !m.is_admin) ? (
        <Card className="p-s8 text-center">
          <Shield className="mx-auto h-8 w-8 text-ink-2" aria-hidden />
          <p className="mt-s3 text-md font-bold">No moderators yet</p>
          <p className="mt-1 text-base text-ink-2">
            Add somebody from the church and choose the role they hold.
          </p>
        </Card>
      ) : null}

      {moderators.data?.length ? (
        <Card className="divide-y divide-line-2">
          {moderators.data.map((m) => {
            const fixed = m.is_admin || m.id === myId
            return (
              <div key={m.id} className="flex flex-wrap items-center gap-s3 p-s3">
                <Avatar name={m.name} />
                {/* At least wide enough to read the name; on a phone the role
                    and Remove wrap onto the line below. */}
                <div className="min-w-[11rem] flex-1">
                  <div className="truncate text-md font-semibold">{m.name}</div>
                  <div className="truncate text-sm text-ink-2">
                    {[m.mobile, m.email].filter(Boolean).join(' · ') || '—'}
                  </div>
                </div>
                {fixed ? (
                  <span className="rounded-pill bg-surface-2 px-s3 py-1 text-sm font-semibold text-ink-2">
                    {m.is_admin ? 'Church administrator' : m.role_name ?? 'Moderator'}
                  </span>
                ) : (
                  <>
                    <SelectField
                      label="Role"
                      size="sm"
                      className="w-[12rem]"
                      value={m.role_id ?? ''}
                      onChange={(e) =>
                        setModerator.mutate({
                          userId: m.id,
                          isModerator: true,
                          roleId: e.target.value || null,
                        })
                      }
                      options={[
                        ...(m.role_id ? [] : [{ value: '', label: 'Moderator' }]),
                        ...roleOptions,
                      ]}
                    />
                    <button
                      onClick={() => setRemoving(m)}
                      aria-label={`Remove ${m.name} as a moderator`}
                      className="grid h-8 w-8 shrink-0 place-items-center rounded-pill text-ink-2 transition-colors hover:bg-hover-2 hover:text-danger"
                    >
                      <UserMinus className="h-4 w-4" aria-hidden />
                    </button>
                  </>
                )}
              </div>
            )
          })}
        </Card>
      ) : null}

      {adding ? (
        <AddModeratorModal
          churchId={churchId}
          roleOptions={roleOptions}
          already={new Set((moderators.data ?? []).map((m) => m.id))}
          myId={myId}
          onClose={() => setAdding(false)}
        />
      ) : null}

      <Confirm
        open={Boolean(removing)}
        onOpenChange={(next) => !next && setRemoving(null)}
        title={removing ? `Remove ${removing.name} as a moderator?` : ''}
        body="They stay in the church as an ordinary member, and lose what their role allowed."
        confirmLabel="Remove"
        loading={setModerator.isPending}
        onConfirm={() =>
          removing &&
          setModerator.mutate(
            { userId: removing.id, isModerator: false },
            { onSuccess: () => setRemoving(null) },
          )
        }
      />
    </div>
  )
}

/** The client's Add form: Name, Mobile, Email, Role, Save. Name is chosen
 *  from the church's members; Mobile and Email come with them. */
function AddModeratorModal({
  churchId,
  roleOptions,
  already,
  myId,
  onClose,
}: {
  churchId: string
  roleOptions: { value: string; label: string }[]
  already: Set<string>
  myId: string
  onClose: () => void
}) {
  const [query, setQuery] = useState('')
  const [picked, setPicked] = useState<Member | null>(null)
  const [roleId, setRoleId] = useState('')
  const search = useMemberSearch(churchId, useDebounced(query, 250))
  const setModerator = useSetModerator(churchId)
  const chosenRole =
    roleId || roleOptions.find((r) => r.label === 'Moderator')?.value || ''
  const found = (search.data ?? []).filter(
    (m) => !already.has(m.id) && m.id !== myId,
  )

  function save() {
    if (!picked || setModerator.isPending) return
    setModerator.mutate(
      { userId: picked.id, isModerator: true, roleId: chosenRole || null },
      { onSuccess: onClose },
    )
  }

  return (
    <Modal
      open
      onOpenChange={(next) => !next && onClose()}
      title="Add moderator"
      width="520px"
      guardClose={Boolean(picked)}
      footer={
        <Button
          variant="primary"
          size="lg"
          full
          onClick={save}
          disabled={!picked}
          loading={setModerator.isPending}
        >
          Save
        </Button>
      }
    >
      <div className="space-y-s3 p-s4">
        {picked ? (
          <>
            <div className="flex items-center gap-s3">
              <Field label="Name" value={picked.name} readOnly className="flex-1" />
              <Button size="sm" onClick={() => setPicked(null)}>
                Change
              </Button>
            </div>
            <Field label="Mobile" value={picked.mobile ?? ''} readOnly />
            <Field label="Email" value={picked.email ?? ''} readOnly />
            <SelectField
              label="Role"
              value={chosenRole}
              onChange={(e) => setRoleId(e.target.value)}
              options={roleOptions}
              help="What they may do. Roles are set up on the Roles tab."
            />
          </>
        ) : (
          <>
            <Field
              label="Name"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              leadIcon={<Search className="h-4 w-4" aria-hidden />}
              help="Search the church's members by name, mobile or email"
              autoFocus
            />
            {query.trim().length >= 2 ? (
              search.isLoading ? (
                <div className="flex justify-center py-s4">
                  <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
                </div>
              ) : found.length ? (
                <ul className="divide-y divide-line-2 rounded-md border border-line-2">
                  {found.map((m) => (
                    <li key={m.id}>
                      <button
                        type="button"
                        onClick={() => setPicked(m)}
                        className="flex w-full items-center gap-s3 px-s3 py-s2 text-left transition-colors hover:bg-hover"
                      >
                        <Avatar name={m.name} size="sm" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-semibold">{m.name}</span>
                          <span className="block truncate text-sm text-ink-2">
                            {m.mobile ?? m.email ?? ''}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-base text-ink-2">
                  Nobody in the church matches that, or they are already a moderator.
                </p>
              )
            ) : null}
          </>
        )}

        {setModerator.isError ? (
          <p className="text-base text-danger">
            {setModerator.error instanceof ApiError
              ? setModerator.error.message
              : 'That did not save.'}
          </p>
        ) : null}
      </div>
    </Modal>
  )
}

/* ------------------------------------------------------------------ roles */

function Roles({ churchId }: { churchId: string }) {
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<ChurchRole | null>(null)
  const [deleting, setDeleting] = useState<ChurchRole | null>(null)
  const roles = useRoles(churchId)
  const remove = useDeleteRole(churchId)
  const catalogue = usePermissionCatalogue()

  // Key → plain-English label, so a role's stored permissions can be shown in
  // the same words the checkboxes used.
  const labels = useMemo(() => {
    const map = new Map<string, string>()
    catalogue.data?.forEach((section) =>
      section.permissions.forEach((p: { key: string; label: string }) =>
        map.set(p.key, p.label),
      ),
    )
    return map
  }, [catalogue.data])

  return (
    <div className="space-y-s3">
      <div className="flex justify-end">
        <Button variant="primary" onClick={() => setAdding(true)}>
          <Plus className="h-4 w-4" aria-hidden />
          Add role
        </Button>
      </div>

      {roles.isLoading ? (
        <div className="flex justify-center py-s6">
          <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
        </div>
      ) : null}

      {!roles.isLoading && !roles.data?.length ? (
        <Card className="p-s8 text-center">
          <Shield className="mx-auto h-8 w-8 text-ink-2" aria-hidden />
          <p className="mt-s3 text-md font-bold">No roles yet</p>
          <p className="mt-1 text-base text-ink-2">
            A role is a named set of things somebody may do.
          </p>
        </Card>
      ) : null}

      {roles.data?.map((r) => (
        <Card key={r.id} className="p-s4">
          <div className="flex items-start gap-s3">
            <div className="min-w-0 flex-1">
              <h3 className="text-md font-bold">{r.name}</h3>
              {r.description ? (
                <p className="mt-1 text-base text-ink-2">{r.description}</p>
              ) : null}
            </div>
            <Button variant="secondary" size="sm" onClick={() => setEditing(r)}>
              <Pencil className="h-4 w-4" aria-hidden />
              Edit
            </Button>
            <button
              onClick={() => setDeleting(r)}
              aria-label={`Delete the ${r.name} role`}
              className="grid h-8 w-8 shrink-0 place-items-center rounded-pill text-ink-2 transition-colors hover:bg-hover-2 hover:text-danger"
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </button>
          </div>
          {r.permissions.length ? (
            <ul className="mt-s3 flex flex-wrap gap-s2">
              {r.permissions.map((p) => (
                <li
                  key={p}
                  className="rounded-pill bg-surface-2 px-s3 py-1 text-sm font-semibold text-ink-2"
                >
                  {labels.get(p) ?? p}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-s2 text-base text-ink-2">
              No permissions — this role can do nothing yet.
            </p>
          )}
        </Card>
      ))}

      {adding ? (
        <RoleModal churchId={churchId} onClose={() => setAdding(false)} />
      ) : null}

      {editing ? (
        <RoleModal
          churchId={churchId}
          role={editing}
          onClose={() => setEditing(null)}
        />
      ) : null}

      <Confirm
        open={Boolean(deleting)}
        onOpenChange={(next) => !next && setDeleting(null)}
        title={deleting ? `Delete the ${deleting.name} role?` : ''}
        body="Anyone carrying it stays a moderator, but loses this role and everything it allowed."
        confirmLabel="Delete role"
        loading={remove.isPending}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) })
        }
      />
    </div>
  )
}

/** One form for creating and for editing.
 *
 *  Two modals would drift: a permission added to one and forgotten in the
 *  other is exactly the kind of gap nobody notices until a role cannot be
 *  given something it could be created with. `role` present means edit. */
function RoleModal({
  churchId,
  role,
  onClose,
}: {
  churchId: string
  role?: ChurchRole
  onClose: () => void
}) {
  const editing = Boolean(role)
  const [name, setName] = useState(role?.name ?? '')
  const [description, setDescription] = useState(role?.description ?? '')
  const [selected, setSelected] = useState<string[]>(role?.permissions ?? [])
  const catalogue = usePermissionCatalogue()
  const create = useCreateRole(churchId)
  const update = useUpdateRole(churchId)
  const saving = create.isPending || update.isPending
  const failed = create.isError || update.isError

  function toggle(key: string) {
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    )
  }

  function submit() {
    if (!name.trim() || saving) return
    const body = {
      name: name.trim(),
      description: description.trim() || null,
      permissions: selected,
    }
    if (role) {
      update.mutate({ roleId: role.id, ...body }, { onSuccess: onClose })
    } else {
      create.mutate(body, { onSuccess: onClose })
    }
  }

  return (
    <Modal
      open
      onOpenChange={(next) => !next && onClose()}
      title={editing ? `Edit ${role!.name}` : 'New role'}
      width="560px"
      guardClose={Boolean(name.trim() || selected.length)}
      footer={
        <Button
          variant="primary"
          size="lg"
          full
          onClick={submit}
          disabled={!name.trim()}
          loading={saving}
        >
          {editing ? 'Save changes' : 'Create role'}
        </Button>
      }
    >
      <div className="space-y-s3 p-s4">
        <Field
          id="role-name"
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Moderator-1"
          autoFocus
          required
        />
        <Field
          id="role-description"
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Looks after the youth group's posts"
        />

        {catalogue.isLoading ? (
          <div className="flex justify-center py-s5">
            <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
          </div>
        ) : null}

        {catalogue.data?.map((section) => (
          <div key={section.section}>
            <h3 className="pb-s1 pt-s2 text-sm font-bold uppercase tracking-wide text-ink-2">
              {section.section}
            </h3>
            <ul className="space-y-s1">
              {section.permissions.map(({ key, label }) => {
                const on = selected.includes(key)
                return (
                  <li key={key}>
                    <button
                      onClick={() => toggle(key)}
                      aria-pressed={on}
                      className={cn(
                        'flex w-full items-center gap-s3 rounded-md border px-s3 py-s2 text-left text-base font-semibold transition-colors',
                        on
                          ? 'border-brand bg-brand-softer text-brand'
                          : 'border-line text-ink hover:bg-hover',
                      )}
                    >
                      <span
                        className={cn(
                          'grid h-5 w-5 shrink-0 place-items-center rounded-sm border',
                          on ? 'border-brand bg-brand text-white' : 'border-line',
                        )}
                        aria-hidden
                      >
                        {on ? <Check className="h-3.5 w-3.5" /> : null}
                      </span>
                      {label}
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}

        {failed ? (
          <p className="text-base text-danger">
            That did not save. A role with that name may already exist.
          </p>
        ) : null}
      </div>
    </Modal>
  )
}
